import { Capacitor } from '@capacitor/core'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { PushNotifications } from '@capacitor/push-notifications'
import { Share } from '@capacitor/share'
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import { Preferences } from '@capacitor/preferences'

const BIOMETRIC_UNLOCK_KEY = 'biometric-unlock-enabled'

export const isNativeApp = () => Capacitor.isNativePlatform()

export const takeProfilePhoto = async (): Promise<string> => {
  const photo = await Camera.getPhoto({
    quality: 85,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Prompt,
    width: 1600,
    correctOrientation: true,
  })
  if (!photo.dataUrl) throw new Error('No photo was returned')
  return photo.dataUrl
}

export const shareCode = async (handle: string) => {
  const url = `https://youwont.xyz/u/${handle}`
  if (isNativeApp() || (await Share.canShare()).value) {
    await Share.share({ title: `@${handle} on YOWO`, text: 'Scan or open my YOWO code', url })
    return
  }
  await navigator.clipboard.writeText(url)
}

export const enablePushNotifications = async (): Promise<boolean> => {
  if (!isNativeApp()) return false
  const permission = await PushNotifications.requestPermissions()
  if (permission.receive !== 'granted') return false
  await PushNotifications.register()
  return true
}

export const biometricUnlock = async (): Promise<boolean> => {
  if (!isNativeApp()) return true
  const status = await BiometricAuth.checkBiometry()
  if (!status.isAvailable) return false
  await BiometricAuth.authenticate({
    reason: 'Unlock your YOWO account',
    allowDeviceCredential: true,
    androidTitle: 'Unlock YOWO',
  })
  return true
}

export const isBiometricUnlockEnabled = async (): Promise<boolean> => {
  if (!isNativeApp()) return false
  return (await Preferences.get({ key: BIOMETRIC_UNLOCK_KEY })).value === 'true'
}

export const setBiometricUnlockEnabled = async (enabled: boolean): Promise<void> => {
  if (!isNativeApp()) return
  if (enabled && !(await biometricUnlock())) throw new Error('Biometrics are unavailable on this device')
  await Preferences.set({ key: BIOMETRIC_UNLOCK_KEY, value: String(enabled) })
}
